import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Input,
  Button,
  Popconfirm,
  Tooltip,
  message,
  Select,
} from 'antd';
import { ArrowLeftOutlined, DeleteOutlined, PlusOutlined, EditOutlined, CalendarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { Spin } from 'antd';
import { useAuth } from '../../contexts/AuthContext';
import {
  getClient,
  updateClient,
  addNote,
  updateNote,
  deleteNote,
  addTrip,
  updateTrip,
  deleteTrip,
  deleteClient,
} from '../../services/clientsService';
import type { Client, Trip } from '../../types/client';
import { clientCrmEn } from '../../content/clientCrm.en';
import { ClientDetailsDrawer } from './ClientDetailsDrawer';
import TripDateRangePicker from '../../components/TripDateRangePicker/TripDateRangePicker';
import styles from './ClientCRM.module.css';

const { TextArea } = Input;

const clientsEnTripDates = (start: string | null, end: string | null): string => {
  if (!start && !end) return '—';
  if (start && end) {
    const s = dayjs(start);
    const e = dayjs(end);
    return `${s.format('MMM D')} - ${e.format('MMM D, YYYY')}`;
  }
  if (start) return dayjs(start).format('MMM D, YYYY');
  if (end) return dayjs(end).format('MMM D, YYYY');
  return '—';
};

const AddNoteForm: React.FC<{
  onSubmit: (values: { noteText: string }) => void;
  placeholder: string;
  addNoteLabel: string;
}> = ({ onSubmit, placeholder, addNoteLabel }) => {
  const [noteText, setNoteText] = useState('');
  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    const text = noteText.trim();
    if (!text) return;
    onSubmit({ noteText: text });
    setNoteText('');
  };
  return (
    <form onSubmit={handleSubmit} className={styles.addNoteForm}>
      <div style={{ marginBottom: 8 }}>
        <TextArea
          rows={3}
          placeholder={placeholder}
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          required
        />
      </div>
      <Button type="primary" htmlType="submit">
        {addNoteLabel}
      </Button>
    </form>
  );
};

const ClientCRM: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [client, setClient] = useState<Client | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const [editingTripId, setEditingTripId] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [clientId]);

  useEffect(() => {
    if (authLoading) return;
    if (!user?.sub || !clientId) {
      setHasLoaded(true);
      return;
    }
    const c = getClient(user.sub, clientId);
    setClient(c ?? null);
    setHasLoaded(true);
  }, [user?.sub, clientId, authLoading]);

  const firstTrip = client?.trips?.[0] ?? null;

  const handleSaveDetails = (clientUpdates: Partial<Client>, tripUpdates: Partial<Trip>, options?: { showToast?: boolean }): void => {
    if (!user?.sub || !clientId || !client) return;
    let merged = client;
    if (Object.keys(clientUpdates).length) {
      const updated = updateClient(user.sub, clientId, clientUpdates);
      if (updated) merged = updated;
    }
    if (firstTrip && Object.keys(tripUpdates).length) {
      const updated = updateTrip(user.sub, clientId, firstTrip.id, tripUpdates);
      if (updated) {
        merged = {
          ...merged,
          trips: (merged.trips ?? []).map((t) => (t.id === firstTrip.id ? updated : t)),
        };
      }
    }
    setClient(merged);
    if (options?.showToast) message.success('Changes saved.');
  };

  const handleAddTrip = (): void => {
    if (!user?.sub || !clientId || !client) return;
    const trips = client.trips ?? [];
    const first = trips[0];
    const isEmptyDefault = trips.length === 1 && first && !first.title && !first.startDate && !first.endDate;
    const newTripData = {
      title: '',
      tripType: null,
      info: '',
      startDate: null,
      endDate: null,
      groupSize: null,
      adults: null,
      children: null,
      childrenAges: [] as number[],
      preferences: {
        airClass: 'any' as const,
        food: [] as Trip['preferences']['food'],
        activityPreferences: [] as Trip['preferences']['activityPreferences'],
        accommodationType: [] as Trip['preferences']['accommodationType'],
        accommodationBudgetPerNight: null,
        travelStyle: 'moderate' as const,
      },
      travelCompanions: [],
    };
    if (isEmptyDefault && first) {
      const newTrip: Trip = { ...newTripData, id: crypto.randomUUID() };
      const updated = updateClient(user.sub, clientId, { trips: [newTrip] });
      if (updated) {
        setClient(updated);
        setEditingTripId(newTrip.id);
      }
    } else {
      const newTrip = addTrip(user.sub, clientId, newTripData);
      if (newTrip && client) {
        setClient({ ...client, trips: [...trips, newTrip] });
        setEditingTripId(newTrip.id);
      }
    }
  };

  const handleUpdateTrip = (tripId: string, updates: Partial<Trip>): void => {
    if (!user?.sub || !clientId || !client) return;
    const updated = updateTrip(user.sub, clientId, tripId, updates);
    if (updated) {
      setClient({
        ...client,
        trips: (client.trips ?? []).map((t) => (t.id === tripId ? updated : t)),
      });
    }
  };

  const handleDoneEditingTrip = (): void => {
    setEditingTripId(null);
  };

  const handleDeleteTrip = (tripId: string): void => {
    if (!user?.sub || !clientId || !client) return;
    if (deleteTrip(user.sub, clientId, tripId)) {
      const refreshed = getClient(user.sub, clientId);
      if (refreshed) setClient(refreshed);
      if (editingTripId === tripId) setEditingTripId(null);
    }
  };

  const handleAddNote = (values: { noteText: string }): void => {
    if (!user?.sub || !clientId) return;
    const text = (values.noteText ?? '').trim();
    if (!text) return;
    const note = addNote(user.sub, clientId, text);
    if (note) {
      setClient((prev) =>
        prev ? { ...prev, notes: [...(Array.isArray(prev.notes) ? prev.notes : []), note] } : null
      );
    }
  };

  const handleStartEditNote = (noteId: string, currentText: string): void => {
    setEditingNoteId(noteId);
    setEditingNoteText(currentText);
  };

  const handleSaveNote = (noteId: string, text: string): void => {
    if (!user?.sub || !clientId) return;
    const updated = updateNote(user.sub, clientId, noteId, text);
    if (updated) {
      setClient((prev) =>
        prev
          ? {
              ...prev,
              notes: (prev.notes ?? []).map((n) => (n.id === noteId ? updated : n)),
            }
          : null
      );
      setEditingNoteId(null);
      setEditingNoteText('');
    }
  };

  const handleCancelEditNote = (): void => {
    setEditingNoteId(null);
    setEditingNoteText('');
  };

  const handleDeleteNote = (noteId: string): void => {
    if (!user?.sub || !clientId) return;
    if (deleteNote(user.sub, clientId, noteId)) {
      setClient((prev) =>
        prev ? { ...prev, notes: (prev.notes ?? []).filter((n) => n.id !== noteId) } : null
      );
      setEditingNoteId(null);
      setEditingNoteText('');
    }
  };

  const handleDeleteClient = (): void => {
    if (!user?.sub || !clientId) return;
    if (deleteClient(user.sub, clientId)) {
      navigate('/clients');
    }
  };

  if (authLoading || (!hasLoaded && !client)) {
    return (
      <div className={styles.clientCrm}>
        <div className={styles.container}>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
            <Spin size="large" />
          </div>
          <Link to="/clients" style={{ display: 'block', textAlign: 'center', marginTop: '1rem' }}>
            {clientCrmEn.actions.back}
          </Link>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className={styles.clientCrm}>
        <div className={styles.container}>
          <p>Client not found.</p>
          <Button type="primary" onClick={() => navigate('/clients')}>
            {clientCrmEn.actions.back}
          </Button>
        </div>
      </div>
    );
  }

  const clientName = [client.firstName, client.lastName].filter(Boolean).join(' ') || 'Client';
  const trips = client.trips ?? [];

  return (
    <div className={styles.clientCrm}>
      <div className={styles.container}>
        <div className={styles.header}>
          <Link to="/clients" className={styles.backLink}>
            <ArrowLeftOutlined /> {clientCrmEn.actions.back}
          </Link>
          <h1 className={styles.title}>{clientName}</h1>
        </div>

        <div className={styles.section}>
          <ClientDetailsDrawer
            client={client}
            trip={firstTrip}
            onSave={handleSaveDetails}
            onDeleteClient={handleDeleteClient}
            onDuplicateFound={(existingId) => navigate(`/clients/${existingId}`)}
            userId={user?.sub ?? undefined}
          />
        </div>

        <div className={styles.section}>
          <div className={styles.sectionTitle}>{clientCrmEn.trips.sectionTitle}</div>
          {trips.length === 0 ? (
            <p className={styles.tripNoTrips}>{clientCrmEn.trips.noTrips}</p>
          ) : (
            <div className={styles.tripList}>
              {trips.map((t) => (
                <div key={t.id} className={styles.tripRow}>
                  {editingTripId === t.id ? (
                    <>
                      <Input
                        placeholder="Trip title"
                        value={t.title}
                        onChange={(e) => handleUpdateTrip(t.id, { title: e.target.value })}
                        autoFocus
                        style={{ flex: 2, minWidth: 120 }}
                      />
                      <Select
                        placeholder={clientCrmEn.trip.tripType}
                        value={t.tripType ?? undefined}
                        onChange={(v) => handleUpdateTrip(t.id, { tripType: v ?? null })}
                        allowClear
                        style={{ flex: 1, minWidth: 140 }}
                        options={Object.entries(clientCrmEn.trip.tripTypeOptions).map(([value, label]) => ({
                          value,
                          label,
                        }))}
                      />
                      <TripDateRangePicker
                        value={{ startDate: t.startDate, endDate: t.endDate }}
                        onChange={(v) => handleUpdateTrip(t.id, { startDate: v.startDate, endDate: v.endDate })}
                        startDatePlaceholder="Start"
                        endDatePlaceholder="End"
                        style={{ flex: 1, minWidth: 220 }}
                      />
                      <Button type="text" size="small" onClick={handleDoneEditingTrip}>
                        Done
                      </Button>
                    </>
                  ) : (
                    <>
                      <span className={styles.tripTitle}>{t.title || 'Untitled'}</span>
                      <span className={styles.tripType}>
                        {t.tripType ? clientCrmEn.trip.tripTypeOptions[t.tripType] : ''}
                      </span>
                      <span className={styles.tripDates}>
                        {clientsEnTripDates(t.startDate, t.endDate)}
                      </span>
                      <div className={styles.tripActions}>
                        {t.startDate && t.endDate && (
                          <Tooltip title={clientCrmEn.trip.planItinerary}>
                            <Link to={`/clients/${clientId}/plan/${t.id}`}>
                              <Button type="text" size="small" icon={<CalendarOutlined />} />
                            </Link>
                          </Tooltip>
                        )}
                        <Tooltip title={clientCrmEn.trip.editTrip}>
                          <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => setEditingTripId(t.id)}
                          />
                        </Tooltip>
                        <Popconfirm
                          title="Delete this trip?"
                          onConfirm={() => handleDeleteTrip(t.id)}
                        >
                          <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
          <Button type="dashed" onClick={handleAddTrip} icon={<PlusOutlined />} style={{ marginTop: 8 }}>
            {clientCrmEn.trip.addTrip}
          </Button>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionTitle}>{clientCrmEn.notes.sectionTitle}</div>
          {(Array.isArray(client.notes) ? client.notes : []).length ? (
            <ul className={styles.notesList}>
              {(Array.isArray(client.notes) ? client.notes : []).map((note) => (
                <li key={note.id} className={styles.noteItem}>
                  {editingNoteId === note.id ? (
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <TextArea
                        rows={3}
                        value={editingNoteText}
                        onChange={(e) => setEditingNoteText(e.target.value)}
                        autoFocus
                      />
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Button size="small" type="primary" onClick={() => handleSaveNote(note.id, editingNoteText.trim())}>
                          Save
                        </Button>
                        <Button size="small" onClick={handleCancelEditNote}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <span className={styles.noteText}>{note.text}</span>
                      <div>
                        <span className={styles.noteMeta}>
                          {new Date(note.createdAt).toLocaleDateString()}
                        </span>
                        <Button
                          type="text"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => handleStartEditNote(note.id, note.text)}
                        />
                        <Popconfirm
                          title="Delete this note?"
                          onConfirm={() => handleDeleteNote(note.id)}
                        >
                          <Button
                            type="text"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                          />
                        </Popconfirm>
                      </div>
                    </>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <div className={styles.noNotes}>{clientCrmEn.notes.noNotes}</div>
          )}
          <AddNoteForm
            onSubmit={handleAddNote}
            placeholder={clientCrmEn.notes.placeholder}
            addNoteLabel={clientCrmEn.notes.addNote}
          />
        </div>
      </div>
    </div>
  );
};

export default ClientCRM;
